import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import { useFooterOrganizer } from "../hooks/useFooterOrganizer";
import "./Footer.css";

const LEGAL_LINKS = [
  { to: "/legal/aviso-legal", key: "legalNotice" },
  { to: "/legal/privacidad", key: "privacy" },
  { to: "/legal/terminos", key: "terms" },
  { to: "/legal/cookies", key: "cookies" },
  { to: "/legal/contrato-encargado", key: "dpa" },
  { to: "/legal/rgpd", key: "rgpd" },
  { to: "/legal/accessibility", key: "accessibility" },
];

function OrganizerBlock({ organizer }) {
  const { t } = useTranslation("footer");
  if (!organizer) return null;

  return (
    <section
      className="footer-organizer"
      aria-label={t("organizerHeading")}
      data-testid="footer-organizer"
    >
      <h3 className="footer-section-title">{t("organizerHeading")}</h3>
      <dl className="footer-organizer-grid">
        {organizer.name && (
          <div className="footer-organizer-row">
            <dt>{t("organizerLabels.name")}</dt>
            <dd>{organizer.name}</dd>
          </div>
        )}
        {organizer.email && (
          <div className="footer-organizer-row">
            <dt>{t("organizerLabels.email")}</dt>
            <dd>
              <a href={`mailto:${organizer.email}`}>{organizer.email}</a>
            </dd>
          </div>
        )}
        {organizer.phone && (
          <div className="footer-organizer-row">
            <dt>{t("organizerLabels.phone")}</dt>
            <dd>
              <a href={`tel:${organizer.phone}`}>{organizer.phone}</a>
            </dd>
          </div>
        )}
        {organizer.cancellationPolicy && (
          <div className="footer-organizer-row">
            <dt>{t("organizerLabels.cancellationPolicy")}</dt>
            <dd>{organizer.cancellationPolicy}</dd>
          </div>
        )}
        {organizer.terms && (
          <div className="footer-organizer-row footer-organizer-row--wide">
            <dt>{t("organizerLabels.terms")}</dt>
            <dd>{organizer.terms}</dd>
          </div>
        )}
      </dl>
    </section>
  );
}

export default function Footer() {
  const { t } = useTranslation("footer");
  const organizer = useFooterOrganizer();
  const year = new Date().getFullYear();

  return (
    <footer className="app-footer" data-testid="app-footer" role="contentinfo">
      <div className="footer-inner">
        <div className="footer-grid">
          <div className="footer-brand-col">
            <Link to="/" className="footer-brand">
              <Logo size={22} className="footer-brand-icon" />
              <span className="footer-brand-name">{t("brand")}</span>
            </Link>
            <p className="footer-tagline">{t("tagline")}</p>
            <p className="footer-nif" data-testid="footer-nif">
              {t("nif")}
            </p>
          </div>

          <nav
            className="footer-links-col"
            aria-label={t("legalHeading")}
            data-testid="footer-legal-links"
          >
            <h3 className="footer-section-title">{t("legalHeading")}</h3>
            <ul className="footer-links">
              {LEGAL_LINKS.map(({ to, key }) => (
                <li key={to}>
                  <Link to={to}>{t(`links.${key}`)}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="footer-lang-col">
            <h3 className="footer-section-title">{t("languageHeading")}</h3>
            <LanguageSwitcher />
          </div>
        </div>

        <OrganizerBlock organizer={organizer} />

        <div className="footer-bottom">
          <small className="footer-copyright">
            {t("copyright", { year })}
          </small>
        </div>
      </div>
    </footer>
  );
}
